import { FormHelperText, FormLabel, Stack } from '@mui/material';
import { ConditionDatesForm, getConditionBindingValuesFromLevelSchema } from '@retail/calculus/components';
import { ActivateAgreementSchema, AgreementSelect } from '@retail/purchase-agreements/components';
import { ItemAgreementPrices, PurchaseAgreement } from '@retail/purchase-agreements/types';
import { useCallback, useMemo } from 'react';
import { useController, useFormContext, useWatch } from 'react-hook-form';
import { SuspenseWithSentryErrorBoundary } from '@retail/components';
import { usePurchaseAgreementsTFunction } from '@retail/purchase-agreements/i18n';
import { ErrorOutlineRounded } from '@mui/icons-material';
import { SelectItemAgreements } from '@retail/purchase-agreements/containers';
import { ConditionsNew } from '@retail/calculus/types';

interface Props {
  assortmentId: number;
  agreements: PurchaseAgreement[];
  supplierNumber: number;
  conditions?: ConditionsNew[];
}

export function CreatePurchaseConditionForm({
  assortmentId,
  agreements,
  supplierNumber,
  conditions,
}: Props) {
  const t = usePurchaseAgreementsTFunction();

  const {
    control,
    formState: { errors },
  } = useFormContext<ActivateAgreementSchema>();
  const levelSchema = useWatch({ control });
  const itemController = useController({ name: 'item', control });
  const agreementIdController = useController({ name: 'agreementId', control });
  const priceTypeController = useController({ name: 'priceType', control });
  const packageTypeController = useController({ name: 'packageType', control });

  const setAgreementValue = useCallback(
    (agreement?: ItemAgreementPrices) => {
      agreementIdController.field.onChange(agreement?.agreementId);
      priceTypeController.field.onChange(agreement?.priceType);
      packageTypeController.field.onChange(agreement?.packageType);
    },
    [agreementIdController.field, packageTypeController.field, priceTypeController.field]
  );

  const setAgreementId = useCallback(
    (agreementId: number) => agreementIdController.field.onChange(agreementId),
    [agreementIdController.field]
  );

  // The condition (if any) that is already active at the currently selected level,
  // matched by comparing the selected binding level with each condition's binding.
  const activeConditionAtLevel = useMemo(() => {
    if (!conditions?.length) return undefined;

    const selectedBindings = getConditionBindingValuesFromLevelSchema({
      levelSchema: levelSchema as ActivateAgreementSchema,
      supplierNumber,
    }).map((binding) => JSON.stringify(binding));

    return conditions.find(
      (condition) =>
        condition.purchaseCondition &&
        selectedBindings.includes(JSON.stringify(condition.getBindingLevel()))
    );
  }, [conditions, levelSchema, supplierNumber]);

  return (
    <form>
      <Stack gap={3}>
        <Stack>
          <FormLabel error={!!errors.agreementId} sx={{ fontWeight: 500 }} required>
            {t('purchaseAgreements.agreement.chooseAgreement')}
          </FormLabel>
          {itemController.field.value ? (
            <SuspenseWithSentryErrorBoundary>
              <SelectItemAgreements
                assortmentId={assortmentId}
                supplierItemId={itemController.field.value.id}
                selectAgreement={setAgreementValue}
              />
            </SuspenseWithSentryErrorBoundary>
          ) : (
            <AgreementSelect
              agreements={agreements}
              selectedAgreement={Number(agreementIdController.field.value)}
              activeConditions={activeConditionAtLevel ? [activeConditionAtLevel] : undefined}
              selectAgreement={setAgreementId}
            />
          )}

          {errors.agreementId && (
            <Stack direction="row" spacing={1} flexWrap="nowrap" alignItems="center">
              <ErrorOutlineRounded color="warning" fontSize="small" />
              <FormHelperText sx={{ color: 'warning.main' }}>
                {t('purchaseAgreements.agreement.actions.chooseAgreement')}
              </FormHelperText>
            </Stack>
          )}
        </Stack>
        <ConditionDatesForm excludeToDate inputFieldWidth={400} />
      </Stack>
    </form>
  );
}
